/**
 * InstanceTable — the EIA portfolio's instances: every row added (or
 * uploaded), with the server's per-cell remarks shown under the cell.
 *
 * Port of legacy InstancesListTable.vue (EIA headers :420-545). The behaviour
 * (remarks, problem-first sort, Delete / Delete Error Records, editing a cell
 * in place, the regions link) is the shared InstanceTableCore, which the CCA
 * table uses too; this file is only EIA's column list and edit rules:
 *  - legacy's columns and titles, OCPU / Memory only for OCI;
 *  - the two-row header, "Maximum Bandwidth Used" over CPU … U95;
 *  - every cell but UUID edits (instanceRows EDITABLE_FIELDS).
 *
 * Props: rows, provider, loading, onDelete, onDeleteErrors, actionsDisabled,
 * onEditCell, onEditStart, onEditingChange, regionOptions,
 * instanceTypeOptions, emptyMessage, regionsHref — see InstanceTableCore.
 *
 * Project rule note (.claude/rules/variants.md): prefer one shared component
 * with EIA-only parts behind <VariantGate only="eia"> over per-product folders.
 */
import { EDITABLE_FIELDS } from '../instanceRows';
import { FIELD } from '../portfolioFormRules';

import { InstanceTableCore } from './InstanceTableCore';

/**
 * Legacy's two-row header (InstancesListTable.vue:44-64): this label spans
 * every column from CPU to U95; the first four columns stand on both rows.
 */
const BANDWIDTH_GROUP = 'Maximum Bandwidth Used';

/** EIA columns, legacy's order and titles. `oci` = only shown for OCI. */
const COLUMNS = Object.freeze([
  { field: FIELD.UUID, header: 'UUID / Instance Name', wide: true },
  { field: FIELD.REGION, header: 'Region' },
  { field: FIELD.INSTANCE_TYPE, header: 'Instance Type' },
  { field: FIELD.OS, header: 'OS Type' },
  { field: FIELD.MAX_CPU, header: 'CPU (%)', numeric: true, group: BANDWIDTH_GROUP },
  { field: FIELD.MAX_MEM, header: 'Memory (GB)', numeric: true, group: BANDWIDTH_GROUP },
  { field: FIELD.MAX_DISK_BW, header: 'Disk (MBps)', numeric: true, group: BANDWIDTH_GROUP },
  { field: FIELD.MAX_NETWORK_BW, header: 'Network (Mbps)', numeric: true, group: BANDWIDTH_GROUP },
  { field: FIELD.MAX_IOPS, header: 'IOPS', numeric: true, group: BANDWIDTH_GROUP },
  { field: FIELD.OCPU, header: 'OCPU', numeric: true, oci: true, group: BANDWIDTH_GROUP },
  {
    field: FIELD.MEMORY,
    header: 'Memory (GB)',
    numeric: true,
    oci: true,
    key: 'oci-memory',
    group: BANDWIDTH_GROUP,
  },
  { field: FIELD.PRICING_MODEL, header: 'Pricing Model', group: BANDWIDTH_GROUP },
  { field: FIELD.UAVG, header: 'UAVG', numeric: true, group: BANDWIDTH_GROUP },
  { field: FIELD.U95, header: 'U95', numeric: true, group: BANDWIDTH_GROUP },
]);

export function InstanceTable(props) {
  return <InstanceTableCore {...props} columns={COLUMNS} editableFields={EDITABLE_FIELDS} />;
}

export default InstanceTable;
